import { ChangeEvent, useCallback, useState } from "react";
import { PaginationItem, Theme, useMediaQuery } from "@mui/material";
import { Pagination } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SelectDate from "./components/SelectDate";
import { DateRange } from "react-day-picker";
import dayjs from "dayjs";
import {
  fetchListReferralService,
  fetchReferralStatisticService,
} from "@/services/referral.service";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "@/constants";
import { EOrderSortBy, ReferralSortBy } from "@/types";

const ReferralPointsPage = () => {
  const [page, setPage] = useState<number>(1);
  const [date, setDate] = useState<DateRange>({
    from: dayjs().subtract(30, "day").toDate(),
    to: dayjs().toDate(),
  });
  const [sortBy, setSortBy] = useState<ReferralSortBy>(ReferralSortBy.DATE);
  const [sortOrder, setSortOrder] = useState<EOrderSortBy>(EOrderSortBy.DESC);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const { data: transactions, isLoading } = useQuery({
    queryKey: [QUERY_KEY.fetchReferralPoints, page, date, sortBy, sortOrder],
    queryFn: () =>
      fetchListReferralService({
        page,
        size: 10,
        startDate: date.from ? dayjs(date.from).format("YYYY-MM-DD") : "",
        endDate: date.to ? dayjs(date.to).format("YYYY-MM-DD") : "",
        sortBy,
        sortOrder,
      }),
  });

  const { data: statistic, isLoading: isLoadingStatistic } = useQuery({
    queryKey: [QUERY_KEY.fetchReferralStatistic],
    queryFn: () => fetchReferralStatisticService(),
  });

  const handleDateChange = (newDate: DateRange) => {
    setDate(newDate);
  };

  const handleChangePage = useCallback(
    (_: ChangeEvent<unknown>, pageNext: number) => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setPage(pageNext);
    },
    [],
  );

  const handleSort = (field: ReferralSortBy) => {
    if (sortBy === field) {
      setSortOrder(
        sortOrder === EOrderSortBy.ASC ? EOrderSortBy.DESC : EOrderSortBy.ASC,
      );
    } else {
      setSortBy(field);
      setSortOrder(EOrderSortBy.DESC);
    }
  };

  const count = transactions?.totalPages || 0;

  return (
    <>
      <div className="-mt-16 flex items-center gap-4 max-sm:mt-0 md:w-1/2">
        <h2 className="text-3xl font-semibold text-text max-sm:text-[18px]">
          Referral Points
        </h2>
      </div>

      <div className="mt-5">
        <div className="mb-6">
          <div className="inline-flex items-center rounded-md border border-[#CBD5E1] bg-white shadow-sm">
            <span className="flex h-12 w-16 items-center justify-center rounded-l-md border-r border-[#CBD5E1] bg-[#F6F9FB] text-sm font-semibold text-text">
              Date
            </span>
            <SelectDate
              mode="range"
              date={date}
              setDateSelect={handleDateChange}
              numberOfMonths={isMobile ? 1 : 2}
              contentStyle={{
                width: isMobile ? "320px" : "",
              }}
              className="border-none"
              handleCloseUserMenu={() => {}}
              handleConform={() => {}}
              handleSelect={() => {}}
              anchorElUser={null}
            />
          </div>
        </div>

        <div className="mb-6 grid grid-cols-3 gap-[26px] max-sm:gap-[6px]">
          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img
                src="/icons/trailing-slot.svg"
                alt="hand-coins"
                className="mr-3"
              />
            ) : (
              <img
                src="/icons/coins-hand.svg"
                alt="hand-coins"
                className="p-1"
              />
            )}

            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Points Earned
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalEarnedPoints || 0
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img src="/icons/star.svg" alt="star" className="mr-3" />
            ) : (
              <img src="/icons/star-01.svg" alt="star" className="p-1" />
            )}
            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Available Points
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalPoints || 0
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-[#F6F9FB] p-4 shadow-sm max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:bg-white max-sm:px-3">
            {!isMobile ? (
              <img
                src="/icons/check-circle.svg"
                alt="check-circle"
                className="mr-3"
              />
            ) : (
              <img
                src="/icons/check-circle-broken.svg"
                alt="check-circle-broken"
                className="p-1"
              />
            )}

            <div className="max-sm:space-y-1">
              <span className="block text-[16px] font-medium text-text max-sm:text-[12px]">
                Points Used
              </span>
              <div className="text-[16px] text-[#334155]">
                {isLoadingStatistic ? (
                  <div className="h-6 w-16 animate-pulse rounded bg-neutral-400" />
                ) : (
                  statistic?.totalClaimedPoints || 0
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-md bg-white shadow-sm">
          <div className="grid grid-cols-2 bg-indigo-600 text-white">
            <div
              className="flex cursor-pointer items-center px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(ReferralSortBy.DATE)}
            >
              <span className="mr-2">Date</span>
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
            <div
              className="flex cursor-pointer items-center px-4 py-3 text-sm font-medium"
              onClick={() => handleSort(ReferralSortBy.TOTAL_POINTS)}
            >
              <span className="mr-2">Points Earned</span>
              <img src={`/icons/chevron-selector-vertical.svg`} alt="arrow" />
            </div>
          </div>

          <div>
            {isLoading
              ? Array(5)
                  .fill(null)
                  .map((_, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-2 border-b border-[#E2E8F0]"
                    >
                      <div className="px-4 py-6">
                        <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
                      </div>
                      <div className="px-4 py-6">
                        <div className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                      </div>
                    </div>
                  ))
              : transactions?.data.map((transaction, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-2 ${index === transactions?.data.length - 1 ? "" : "border-b border-[#E2E8F0]"}`}
                  >
                    <div className="px-4 py-6 text-sm text-text">
                      {dayjs(transaction.date).format("YYYY/MM/DD")}
                    </div>
                    <div className="px-4 py-6 text-sm text-text">
                      {transaction.total_points}{" "}
                      {transaction.total_points < 2 ? "Point" : "Points"}
                    </div>
                  </div>
                ))}
          </div>

          {!!count && (
            <div className="flex justify-center py-4">
              <Pagination
                shape="rounded"
                count={count}
                page={page}
                onChange={handleChangePage}
                siblingCount={1}
                boundaryCount={1}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                    sx={{
                      backgroundColor: "transparent",
                      color: "#94A3B8",
                      fontSize: 16,
                      "&.Mui-selected": {
                        backgroundColor: "#E0E7FF",
                        color: "#4F46E5",
                        fontWeight: "bold",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                    }}
                    {...item}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReferralPointsPage;
